import {
  Icon,
  IconButton,
  Typography,
  useIsMobileOrTablet
} from '@epilot/concorde-elements'
import type {
  EmbedOptions,
  Journey,
  StepState,
  ValueError
} from '@epilot/journey-logic-commons'
import {
  STEP_MAX_WIDTH_MAP,
  STEP_MAX_WIDTH_OPTIONS
} from '@epilot/journey-logic-commons'
import classNames from 'classnames'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'

import { usePortalCheck } from '../../../blocks-renderers'
import {
  JourneyStepper as ConcordeJourneyStepper,
  TopBar as ConcordeTopBar
} from '../../../concorde-renderers'
import type { JourneyStepperProps } from '../../../concorde-renderers/renderers/components/JourneyStepper/types'
import { ID_TYPE, uuidToHtmlId } from '../../../concorde-renderers/utils'
import { useAppStoreSelector } from '../../../hooks/useAppStoreSelector'
import { useDarkModeToggle } from '../../../hooks/useDarkMode'
import {
  selectAppParamsOrThrows,
  selectDarkMode
} from '../../../store/selectors/runtime'
import { DisplayJson } from '../../DisplayJson'

import classes from './LinearJourney.module.scss'
import type { StepComponentProps } from './StepComponent'
import { StepComponent } from './StepComponent'

interface LinearProgresStyle extends CSSProperties {
  '--concorde-default-layout-max-width'?: string
}

export type LinearJourneyProps = {
  isFocusedOnJourney: boolean
  journey: Journey
  currentStepIndex: number
  debug?: boolean
  onNavigateToStep: JourneyStepperProps['onChangeStepIndex']
  onGoBack: () => void
  onCloseButtonClicked: () => void
  onChange: StepComponentProps['onChange']
  stepsState: StepState[]
  stepsErrors: ValueError[][]
  showTopBar?: boolean
  showCloseButton?: boolean
  isLinearJourney?: boolean
  submitJourneySuccess?: boolean
  mode: EmbedOptions['mode']
  isParentLauncherJourney?: boolean
  parentJourneyId?: string
  isPreview?: boolean
}

export const LinearJourney = ({
  currentStepIndex,
  debug,
  isFocusedOnJourney,
  isLinearJourney,
  submitJourneySuccess,
  mode,
  showCloseButton,
  stepsErrors,
  isParentLauncherJourney,
  isPreview,
  journey,
  onChange,
  onCloseButtonClicked,
  onGoBack,
  onNavigateToStep,
  parentJourneyId,
  showTopBar = true,
  stepsState
}: LinearJourneyProps) => {
  const { t } = useTranslation()
  const isDarkMode = useAppStoreSelector(selectDarkMode)
  const toggle = useDarkModeToggle()
  const { isECPPortal } = usePortalCheck()
  const { isEmbedded } = useAppStoreSelector(selectAppParamsOrThrows)

  const isJourneyEmbedded = !isPreview && isEmbedded
  // Stepper type
  const isMobileOrTablet = useIsMobileOrTablet()
  const currentStep = journey.steps[currentStepIndex]
  const isTopBarEnabled = mode === 'inline' ? showTopBar : true
  const shouldShowProgressBar = isLinearJourney === false
  const isTopBarBackButton = shouldShowProgressBar || !isMobileOrTablet

  const data = stepsState[currentStepIndex] ?? {}

  const showInactiveJourneyWarning =
    journey?.settings?.isActive === false && !isPreview

  const stepId = currentStep?.stepId

  const topBarProps = {
    isProgressBar: shouldShowProgressBar,
    isTopBarEnabled,
    journey,
    onBack:
      !submitJourneySuccess && isTopBarBackButton && currentStepIndex > 0
        ? onGoBack
        : undefined,
    onClose:
      mode === 'inline' &&
      !(isParentLauncherJourney || parentJourneyId) &&
      showCloseButton !== true
        ? undefined
        : onCloseButtonClicked,

    stepIndex: currentStepIndex
  }

  const customStyle: LinearProgresStyle = {
    '--concorde-default-layout-max-width':
      STEP_MAX_WIDTH_MAP[currentStep?.maxWidth || STEP_MAX_WIDTH_OPTIONS.LARGE]
  }

  return (
    <main
      className={classNames(
        'Concorde-Step',
        classes.concordeStepContainer,
        showInactiveJourneyWarning && classes.inactiveStatusContainer
      )}
      id={uuidToHtmlId(ID_TYPE.STEP, stepId)}
      style={customStyle}
    >
      {isFocusedOnJourney && (
        <ConcordeTopBar
          {...topBarProps}
          showBackButton={currentStepIndex > 0}
        />
      )}

      {journey?.settings?.enableDarkMode &&
        !isECPPortal &&
        !isJourneyEmbedded && (
          <IconButton
            aria-label={
              isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
            }
            className={classNames(
              classes.darkModeButton,
              shouldShowProgressBar && classes.withProgressBar
            )}
            color={isDarkMode ? 'white' : 'primary'}
            name={isDarkMode ? 'dark_mode' : 'light_mode'}
            onClick={toggle}
            size="24px"
          />
        )}

      <Typography as="h1" className="sr-only">
        {journey.name}
      </Typography>

      {showInactiveJourneyWarning && (
        <div className={classes.inactiveStatusInfo}>
          <Icon color="black" name="warning" />
          &nbsp;&nbsp;
          <span>
            {t(
              'inactiveJourneyStatusWarningMessage',
              'Inactive journey, activate it in the journey builder to use it outside a test environment'
            )}
          </span>
        </div>
      )}

      {currentStep?.showStepper && isFocusedOnJourney && (
        <ConcordeJourneyStepper
          isProgressBar={shouldShowProgressBar}
          onChangeStepIndex={onNavigateToStep}
        />
      )}

      {currentStep && (
        <StepComponent
          data={data}
          key={currentStep.stepId} // ref: https://e-pilot.atlassian.net/browse/STABLE360-3452
          onChange={onChange}
          step={{ ...currentStep, stepIndex: currentStepIndex }}
        />
      )}

      {debug && (
        <>
          <DisplayJson data={stepsState[currentStepIndex]} />
          <DisplayJson data={stepsErrors[currentStepIndex]} />
        </>
      )}
    </main>
  )
}

LinearJourney.displayName = 'LinearJourney'
